import { useTranslation } from "react-i18next";
import DataTable from "./DataTable";
import { FC, memo, ReactElement, useCallback, useMemo, useState } from "react";
import UserAvatar from "@/components/controls/UserAvatar";
import { UserData } from "@/entities/users/types";
import { ColumnDef, SortingState } from "@tanstack/react-table";
import { withDealsDrilldown } from "@/components/layouts/DealsDrilldownModal/withDealsDrilldown";
import Text from "@/components/ui/text";

export interface ScoringTableDataRow {
  user: UserData;
  scoreIndicator: [number, number, number?];
  summaryValue: number | string;
}

export interface ScoringTableSimplifiedColumn {
  name: string;
  size?: number;
  textAlign?: "start" | "center" | "end";
  formatter?: (value: any) => string | ReactElement;
}

export interface ScoringTableDef {
  simpleColumns: [
    ScoringTableSimplifiedColumn,
    ScoringTableSimplifiedColumn,
    ScoringTableSimplifiedColumn,
    ScoringTableSimplifiedColumn?
  ];
  summaryColumn: ColumnDef<any, any>;
  initialSummarySortDesc: boolean;
}

interface Props {
  tableDef: ScoringTableDef;
  data: ScoringTableDataRow[];
  onRowClick?: (rowData: ScoringTableDataRow) => void;
  maxRowsInPage: number;
  onScoreClick?: (user: UserData | null) => void;
  isLoading?: boolean;
}

export const UserCell = memo(
  ({ userData, showEmail }: { userData: UserData; showEmail?: boolean }) => {
    const { t } = useTranslation();
    const userDisplayName = userData ? userData.displayName : "";

    return (
      <div className="flex flex-row items-center justify-start px-2 gap-2 py-1 rounded-md w-fit">
        <UserAvatar
          userData={userData}
          fallbackClassName="bg-gradient-to-t from-slate-600 to-tertiary"
        />
        <div className="flex flex-col">
          <Text variant="body" className="px-2">
            {userDisplayName}
          </Text>
          {showEmail && (
            <Text
              variant="smBodySecondary"
              className="px-2 min-h-[28px] items-center flex"
              copyable
              copyLabels={{ tooltip: t("copyTooltip"), copied: t("copied") }}
            >
              {userData?.email}
            </Text>
          )}
        </div>
      </div>
    );
  }
);

UserCell.displayName = "UserCell";

const ScoreCell = memo(
  ({
    value
    // onScoreClick,
    // data,
  }: {
    value: number;
    onScoreClick?: (user: UserData | null) => void;
    data: ScoringTableDataRow[];
  }) => {
    // const handleClick = useCallback(
    //   (e: React.MouseEvent) => {
    //     e.stopPropagation();
    //     const rowData = data.find((row) => row.scoreIndicator[1] === value);
    //     onScoreClick?.(rowData?.user || null);
    //   },
    //   [value, data, onScoreClick]
    // );

    // TODO: pass onClick={handleClick} after we implement the scores modal

    return (
      <div className="flex w-full justify-center">
        <div className="w-fit rounded-md px-2 py-1">{value}</div>
      </div>
    );
  }
);

ScoreCell.displayName = "ScoreCell";

const ScoringTable: FC<Props> = ({
  tableDef,
  data,
  onRowClick,
  maxRowsInPage,
  onScoreClick,
  isLoading
}) => {
  const { simpleColumns, summaryColumn, initialSummarySortDesc } = tableDef;

  const [sorting, setSorting] = useState<SortingState>([
    { id: "col_5", desc: initialSummarySortDesc }
  ]);

  const baseColumns = useMemo(
    () => [
      {
        id: "col_1",
        accessorKey: "user",
        size: 130,
        maxSize: 250,
        cell: ({ getValue }: { getValue: () => UserData }) => (
          <UserCell userData={getValue()} />
        ),
        meta: {
          textAlign: "start"
        }
      },
      {
        id: "col_2",
        accessorKey: "scoreIndicator.0",
        size: 85,
        meta: {
          textAlign: "center"
        }
      },
      {
        id: "col_3",
        accessorKey: "scoreIndicator.1",
        size: 85,
        cell: ({ getValue }: { getValue: () => number }) => (
          <ScoreCell
            value={getValue()}
            onScoreClick={onScoreClick}
            data={data}
          />
        )
      },
      {
        id: "col_4",
        accessorKey: "scoreIndicator.2",
        size: 85,
        meta: {
          textAlign: "center"
        }
      }
    ],
    []
  );

  const instanceColumns = useMemo(() => {
    return baseColumns.slice(0, simpleColumns.length).map((column, index) => {
      const simpleColumn = simpleColumns[index];
      if (!simpleColumn) return column;

      return {
        ...column,
        header: simpleColumn.name,
        size: simpleColumn.size || column.size || 85,
        meta: {
          ...column.meta,
          textAlign:
            simpleColumn.textAlign || column.meta?.textAlign || "center"
        },
        cell: (props: { getValue: () => any }) => {
          const value = props.getValue();
          if (simpleColumn.formatter) {
            return simpleColumn.formatter(value);
          }
          return column.cell ? column.cell(props) : value;
        }
      };
    });
  }, [simpleColumns, baseColumns]);

  const finalColumns = useMemo(
    () => [
      ...instanceColumns,
      { ...summaryColumn, id: "col_5", enableSorting: true }
    ],
    [instanceColumns, summaryColumn]
  );

  const tableOptions = useMemo(
    () => ({
      initialState: {
        pagination: {
          pageIndex: 0,
          pageSize: maxRowsInPage
        }
      },
      onSortingChange: setSorting,
      enableSorting: true
    }),
    [maxRowsInPage, finalColumns]
  );

  const handleRowClick = useCallback(
    (row: any) => {
      const directReporters = row?.original?.scoreIndicator[0];
      directReporters > 0 ? onRowClick?.(row.original) : undefined;
    },
    [onRowClick]
  );

  const handleRowHover = useCallback((row: any) => {
    const directReporters = row?.original?.scoreIndicator[0];
    return directReporters > 0;
  }, []);

  return (
    <div className="w-full px-0 pt-0 h-full">
      <DataTable
        isInfiniteScroll={true}
        columns={finalColumns}
        data={data}
        initialSortBy={sorting}
        tableOptions={tableOptions}
        onRowClick={onRowClick ? handleRowClick : undefined}
        showRowHover={handleRowHover}
        isDataLoading={isLoading}
        headerClassName="border-b border-border-border1 bg-surface-elevation1"
        className="h-full"
      />
    </div>
  );
};

export default memo(withDealsDrilldown(ScoringTable));
